import { formatMoney } from "utils/format-money";

import { type HolderConfigApi, HolderConfiguration } from "./holder-configuration";

type Validator = { id: number; name: string };

type RateJson = {
  id: number;
  name: string;
  price: number;
  availability: number;
  serviceCharge: number;
  max: number | null;
  min: number | null;
  defaultHeld: { id: number } | null;
  validator: Validator | null;
  holderConfiguration: HolderConfigApi;
  weight: number | undefined;
  facilityFee: number | null;
};

class Rate {
  private readonly id: number;
  private readonly name: string;
  private readonly price: number;
  private readonly availability: number;
  private readonly serviceCharge: number;
  private readonly max: number | null;
  private readonly min: number | null;
  private readonly inHold: boolean;
  private readonly emitFromHold: { id: number } | null;
  private readonly validator: Validator | null;
  private readonly holderConfiguration: HolderConfiguration;
  private readonly weight: number;
  private readonly facilityFee: number | null;

  static fromJson(json: RateJson, inHold: boolean, noServiceCharge = false) {
    return new Rate(
      json.id,
      json.name,
      json.price,
      noServiceCharge ? 0 : json.serviceCharge,
      json.availability,
      json.min,
      json.max,
      json.weight ?? 100,
      json.defaultHeld,
      json.validator,
      new HolderConfiguration(json.holderConfiguration),
      inHold,
      json.facilityFee,
    );
  }

  constructor(
    id: number,
    name: string,
    price: number,
    serviceCharge: number,
    availability: number,
    rateMin: number | null,
    rateMax: number | null,
    weight: number,
    defaultHeld: { id: number } | null,
    validator: Validator | null,
    holderConfiguration: HolderConfiguration,
    inHold: boolean,
    facilityFee: number | null,
  ) {
    this.id = id;
    this.name = name;
    this.price = price;
    this.availability = availability;
    this.serviceCharge = serviceCharge;
    this.emitFromHold = defaultHeld;
    this.weight = weight;
    this.validator = validator;
    this.holderConfiguration = holderConfiguration;
    this.inHold = inHold;
    this.facilityFee = facilityFee;

    const min = !availability ? null : !rateMin ? 1 : rateMin > availability ? null : rateMin;
    const max = !availability ? null : !rateMax ? availability : Math.min(rateMax, availability);

    if (min && max && min <= max) {
      this.max = max;
      this.min = min;
    } else {
      this.max = null;
      this.min = null;
    }
  }

  getId() {
    return this.id;
  }

  getName() {
    return this.name;
  }

  matchName(name: string) {
    return this.name.toUpperCase().includes(name.toUpperCase());
  }

  getMin() {
    return this.min;
  }

  getMax() {
    return this.max;
  }

  isQuantityValid(quantity: number) {
    return !this.max || quantity <= this.max;
  }

  getPrice() {
    return this.price;
  }

  getFacilityFee() {
    return this.facilityFee;
  }

  getServiceCharge() {
    return this.serviceCharge;
  }

  getTotalPrice() {
    return this.price + this.serviceCharge;
  }

  getShortDescription() {
    return `${formatMoney(this.price)} + ${formatMoney(this.serviceCharge)}`;
  }

  getDescription() {
    return `${this.name} - ${this.getShortDescription()}`;
  }

  getAvailability() {
    return this.availability;
  }

  getValidatorName() {
    return this.validator?.name;
  }

  isAvailableForGA() {
    return Boolean(this.min && this.max);
  }

  isAvailable() {
    return Boolean(this.min && this.max && this.isValidHold());
  }

  isHolderEnabled() {
    return this.holderConfiguration.isEnabled();
  }

  isHolderSkippable() {
    return this.holderConfiguration.isSkippable();
  }

  hasValidator() {
    return Boolean(this.validator);
  }

  isValidHold() {
    return !this.inHold || !this.emitFromHold;
  }

  private differentValidator(rate: Rate) {
    return Boolean(this.validator && rate.validator && this.validator.id !== rate.validator.id);
  }

  private differentHold(rate: Rate) {
    return Boolean(
      this.emitFromHold && rate.emitFromHold && this.emitFromHold.id !== rate.emitFromHold.id,
    );
  }

  isSelectableWith(rates: Rate[], isNumbered: boolean) {
    if (rates.length === 0) return true;

    if (isNumbered) {
      const holdId = rates.find((rate) => rate.emitFromHold)?.emitFromHold?.id;

      if ((holdId && this.emitFromHold?.id !== holdId) ?? (!holdId && this.emitFromHold?.id)) {
        return false;
      }
    }

    return !rates.some(
      (rate) => this.differentValidator(rate) || (isNumbered ? this.differentHold(rate) : false),
    );
  }

  compareWith(rate: Rate) {
    return this.weight !== rate.weight ? this.weight - rate.weight : rate.price - this.price;
  }
}

export type { RateJson };
export { Rate };
